import { Pane } from 'tweakpane';
import { computed } from '@utils/state/index.js';
import grid from './Grid.js';

/**
 * Debug manager using Tweakpane
 * Handles FPS graph, WebGL stats, and page-specific debug controls
 *
 * Only initializes in development mode (import.meta.env.DEV)
 */
export default class DebugManager {
  constructor() {
    // Check if we're in development mode
    this.isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

    // Don't initialize in production unless forced
    if (!this.isDev && !import.meta.env.VITE_FORCE_DEBUG) {
      console.log('🔒 Debug panel disabled in production');
      this.disabled = true;
      this.exposeEmptyAPI();
      return;
    }

    this.disabled = false;

    // Create main pane
    this.pane = new Pane({ title: 'Debug Panel' });

    // Folders
    this.folders = {};

    // Tabs — General (layout/page/preloader) and WebGL (renderer/quality) kept separate
    const tab = this.pane.addTab({
      pages: [
        { title: 'General' },
        { title: 'WebGL' },
      ]
    });
    this.pages = { general: tab.pages[0], webgl: tab.pages[1] };

    // Create base folders
    this.createBaseFolders();

    // Setup visibility toggle
    this.isVisible = !this.isMobile();
    this.setupVisibilityToggle();

    // Initial visibility
    this.setVisibility(this.isVisible);

    // Expose to window.DEV
    if (!window.DEV) window.DEV = {};
    window.DEV.gui = this;

    console.log('🎨 DebugManager initialized');
  }

  /**
   * Check if device is mobile
   */
  isMobile() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  /**
   * Setup keyboard shortcut and button for visibility toggle
   */
  setupVisibilityToggle() {
    // Keyboard shortcut: 't' key
    document.addEventListener('keydown', (e) => {
      if (e.key === 't') {
        this.toggleVisibility();
      }
    });

    // Create mobile toggle button if needed
    if (this.isMobile()) {
      this.createMobileButton();
    }
  }

  /**
   * Create mobile toggle button
   */
  createMobileButton() {
    const button = document.createElement('button');
    button.className = 'debug-toggle-btn';
    button.textContent = 'Debug';
    button.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9998;
      padding: 10px 15px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      border: 1px solid #333;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
    `;

    button.addEventListener('click', () => {
      this.toggleVisibility();
      button.textContent = this.isVisible ? 'Hide Debug' : 'Show Debug';
    });

    document.body.appendChild(button);
  }

  /**
   * Toggle visibility
   */
  toggleVisibility() {
    this.isVisible = !this.isVisible;
    this.setVisibility(this.isVisible);
  }

  /**
   * Set visibility
   */
  setVisibility(visible) {
    const paneEl = this.pane.element;
    if (paneEl) {
      paneEl.style.display = visible ? 'block' : 'none';
    }
  }

  /**
   * Create base folders for Page
   */
  createBaseFolders() {
    const layoutFolder = this.pages.general.addFolder({
      title: 'Layout',
      expanded: false
    });

    layoutFolder.addButton({
      title: 'Toggle Grid (Cmd+G)'
    }).on('click', () => {
      grid.toggle();
    });

    this._bpState = { width: window.innerWidth, breakpoint: '—' };
    layoutFolder.addBinding(this._bpState, 'width', { readonly: true, label: 'Width' });
    this._bpBinding = layoutFolder.addBinding(this._bpState, 'breakpoint', {
      readonly: true,
      label: 'Breakpoint'
    });

    this.folders.page = this.pages.general.addFolder({
      title: 'Page',
      expanded: false
    });
  }


  updateBreakpoints(width, name) {
    if (!this._bpState || !this._bpBinding) return;
    this._bpState.width = width;
    this._bpState.breakpoint = name;
    this._bpBinding.refresh();
  }

  createWebGLGUI(renderer) {
    if (!renderer) return

    if (this.folders.webgl) {
      this.folders.webgl.dispose();
    }

    this.folders.webgl = this.pages.webgl.addFolder({
      title: 'Renderer',
      expanded: false
    });

    const folder = this.folders.webgl;

    this.webglBindings = [
      folder.addBinding(renderer.info.render, 'calls', {
        readonly: true,
        label: 'Draw Calls',
        format: (v) => Math.round(v)
      }),
      folder.addBinding(renderer.info.memory, 'geometries', {
        readonly: true,
        label: 'Geometries',
        format: (v) => Math.round(v)
      }),
      folder.addBinding(renderer.info.memory, 'textures', {
        readonly: true,
        label: 'Textures',
        format: (v) => Math.round(v)
      }),
      folder.addBinding(renderer.info.programs, 'length', {
        readonly: true,
        label: 'Programs'
      }),
    ];
  }


  createComponentGUI(component) {
    if (!this.folders.page) return null
    return this.folders.page.addFolder({
      title: component.constructor.name,
      expanded: false
    })
  }


  /**
   * Create Page GUI
   */
  createPageGUI(page) {
    if (this.folders.page) {
      this.folders.page.dispose();
      this.folders.page = this.pages.general.addFolder({
        title: 'Page',
        expanded: true
      });
    }

    const folder = this.folders.page;

    folder.addBinding(page, 'isReady', { readonly: true, label: 'Is Ready?' });
    folder.addBinding(page, 'isEntering', { readonly: true, label: 'Is Entering?' });
    folder.addBinding(page, 'isLeaving', { readonly: true, label: 'Is Leaving?' });

    console.log(`✅ Page GUI created for ${page.constructor.name}`);
  }


  /**
   * Wires the QualityManager to the debug panel.
   * Creates a live stats overlay and a Tweakpane Quality folder.
   *
   * @param {import('../performance/QualityManager.js').default} qm
   */
  initQuality(qm) {
    this.qualityManager = qm
    this.createQualityOverlay(qm)
    this.createQualityGUI(qm)
  }

  createQualityOverlay(qm) {
    const el = document.createElement('div')
    el.style.cssText = [
      'position:fixed',
      'top:4px',
      'left:4px',
      'padding:3px 7px',
      'z-index:9999',
      'background:rgba(0,0,0,0.45)',
      'color:#fff',
      'font-size:10px',
      'line-height:1.4',
      'font-family:"Roboto Mono",Menlo,Consolas,monospace',
      'pointer-events:none',
      'border-radius:3px',
      'white-space:pre',
      'text-rendering:optimizeSpeed',
    ].join(';')

    document.body.appendChild(el)
    this.qualityOverlay = el

    computed(
      [qm.fps$, qm.quality$, qm.cpuTime$, qm.gpuTime$, qm.refreshRate$],
      () => this.updateQualityOverlay(qm, el)
    )
  }

  updateQualityOverlay(qm, el) {
    const timestamp = typeof __PROJECT_TIMESTAMP__ !== 'undefined' ? __PROJECT_TIMESTAMP__ : ''

    const lines = [
      qm.qualityName.padEnd(8),
      'FPS ' + String(qm.fps).padStart(3) + '/' + (qm.refreshRate ? qm.refreshRate + 'Hz' : '-'),
      'CPU ' + qm.cpuTime.toFixed(2),
    ]

    if (qm.hasGpuTimer) {
      lines.push('GPU ' + qm.gpuTime.toFixed(2))
    }

    lines.push(timestamp.slice(4))

    el.textContent = lines.join('  ')
  }

  initPreloader() {
    const STORAGE_KEY = 'debug:skip-preloader';
    const state = { skip: sessionStorage.getItem(STORAGE_KEY) === 'true' };

    const folder = this.pages.general.addFolder({ title: 'Preloader', expanded: true });
    this.folders.preloader = folder;

    folder.addBinding(state, 'skip', { label: 'Skip on reload' }).on('change', (ev) => {
      if (ev.value) sessionStorage.setItem(STORAGE_KEY, 'true');
      else sessionStorage.removeItem(STORAGE_KEY);
    });
  }

  createQualityGUI(qm) {
    const folder = this.pages.webgl.addFolder({ title: 'Quality', expanded: false })
    this.folders.quality = folder

    const stats = { fps: qm.fps, avg: 0, level: qm.quality, name: qm.qualityName, refreshRate: qm.refreshRate }

    const fpsB = folder.addBinding(stats, 'fps', { readonly: true, label: 'FPS' })
    const avgB = folder.addBinding(stats, 'avg', { readonly: true, label: 'FPS Avg' })
    const hzB = folder.addBinding(stats, 'refreshRate', { readonly: true, label: 'Display Hz' })
    const lvlB = folder.addBinding(stats, 'level', { readonly: true, label: 'Level (0-5)' })
    const nameB = folder.addBinding(stats, 'name', { readonly: true, label: 'Quality' })

    const prevOnFps = qm.onFpsUpdate
    qm.onFpsUpdate = (fps, avg, quality) => {
      if (prevOnFps) prevOnFps(fps, avg, quality)
      stats.fps = fps
      stats.avg = Math.round(avg)
      stats.level = quality
      stats.name = qm.qualityName
      stats.refreshRate = qm.refreshRate
      fpsB.refresh(); avgB.refresh(); hzB.refresh(); lvlB.refresh(); nameB.refresh()
    }

    const prevOnFps2 = qm.onFpsUpdate
    qm.onFpsUpdate = (fps, avg, quality) => {
      if (prevOnFps2) prevOnFps2(fps, avg, quality)
      if (this.webglBindings) {
        for (const b of this.webglBindings) b.refresh()
      }
    }

    const override = { quality: qm.quality }
    folder.addBinding(override, 'quality', {
      label: 'Force Level',
      min: 0, max: 5, step: 1
    }).on('change', (ev) => {
      qm.forceQuality(ev.value)
    })

    folder.addButton({ title: 'Reset & Remeasure' }).on('click', () => {
      qm.hardReset()
    })
  }


  /**
   * Dispose of the pane
   */
  dispose() {
    if (this.pane) {
      this.pane.dispose();
    }
  }

  /**
   * Expose empty API when debug is disabled
   * Prevents errors in production when code calls debug methods
   */
  exposeEmptyAPI() {
    // Expose to window.DEV
    if (!window.DEV) window.DEV = {};
    window.DEV.gui = this;

    // Create no-op methods
    const noop = () => {};

    // Folders mock
    this.folders = {};

    // Method mocks
    this.createBaseFolders = noop;
    this.createWebGLGUI = noop;
    this.createPageGUI = noop;
    this.createComponentGUI = () => null;
    this.initQuality = noop;
    this.initPreloader = noop;
    this.updateBreakpoints = noop;
    this.toggleVisibility = noop;
    this.setVisibility = noop;
  }
}
